import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { CaseStatus } from '../types';

const ALL_STATUSES: { value: CaseStatus; cls: string }[] = [
  { value: 'open', cls: 'b-open' },
  { value: 'pending', cls: 'b-pending' },
  { value: 'closed', cls: 'b-closed' },
  { value: 'archived', cls: 'b-closed' },
];

/**
 * Badge that opens a status-picker menu on click. The menu is portaled to
 * <body> so the parent's overflow:hidden (e.g. .full-panel) doesn't clip it.
 */
export function StatusBadge({
  caseId,
  status,
  onChanged,
}: {
  caseId: string;
  status: CaseStatus;
  onChanged: (newStatus: CaseStatus) => void;
}) {
  const { t, i18n } = useTranslation('cases');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position the menu under the badge using viewport coords. RTL aligns to
  // the badge's right edge; LTR to its left.
  useLayoutEffect(() => {
    if (!open) return;
    const badge = badgeRef.current;
    const menu = menuRef.current;
    if (!badge) return;
    const rect = badge.getBoundingClientRect();
    const isRtl = document.documentElement.dir === 'rtl';
    const menuWidth = menu?.offsetWidth ?? 130;
    const left = isRtl ? rect.right - menuWidth : rect.left;
    setCoords({ top: rect.bottom + 6, left });
  }, [open, i18n.language]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (badgeRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const current = ALL_STATUSES.find((s) => s.value === status) ?? ALL_STATUSES[0]!;

  async function pick(e: MouseEvent, value: CaseStatus) {
    e.stopPropagation();
    if (value === status) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/api/cases/${caseId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: value }),
      });
      onChanged(value);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span
        ref={badgeRef}
        className={`badge ${current.cls} ${busy ? 'busy' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!busy) setOpen((o) => !o);
        }}
        style={{ cursor: 'pointer' }}
      >
        {busy ? '…' : t(`statuses.${current.value}`)}
      </span>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            className="status-menu status-menu-portal"
            style={{ top: coords.top, left: coords.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {ALL_STATUSES.map((s) => (
              <div
                key={s.value}
                className={`status-menu-item ${s.value === status ? 'active' : ''}`}
                onClick={(e) => pick(e, s.value)}
              >
                <span className={`badge ${s.cls}`}>{t(`statuses.${s.value}`)}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
